import { useState, useCallback } from 'react';

import { useModifyPasswordMutation } from '@features/user/hook';

import Alert from '@shared/components/atoms/Alert';
import Button from '@shared/components/atoms/Button';
import ContentCard from '@shared/components/organisms/ContentCard/ContentCard';
import MainLayout from '@shared/components/organisms/MainLayout/MainLayout';
import { MobileLayout } from '@shared/components/organisms/mobile';
import { useAlert, useIsMobile } from '@shared/hooks';
import eyeClosedIcon from '@shared/assets/images/eye-closed_icon.svg';
import eyeOpenIcon from '@shared/assets/images/eye-open_icon.svg';
import usePasswordChangeForm from '@features/user/hook/usePasswordChangeForm';

import MobilePasswordSettingContent from '@pages/settings/components/mobile/MobilePasswordSettingContent';

import styles from './PasswordSettingPage.module.scss';

/**
 * PasswordSettingPage 컴포넌트
 * - 패스워드설정 페이지
 */
function PasswordSettingPage() {
  // 모바일 화면 감지
  const isMobile = useIsMobile();

  const {
    formData,
    showPassword,
    alertState,
    isPending,
    handleInputChange,
    toggleShowPassword,
    submit,
    closeAlert,
  } = usePasswordChangeForm();

  // 훅은 조건부 return 이전에 항상 호출되어야 함 (Rules of Hooks)
  const handleSubmit = useCallback(
    function (e) {
      e.preventDefault();
      submit();
    },
    [submit],
  );

  // 모바일 레이아웃 렌더링
  if (isMobile) {
    return (
      <MobileLayout title="비밀번호 설정">
        <MobilePasswordSettingContent />
      </MobileLayout>
    );
  }

  return (
    <MainLayout>
      <ContentCard className={styles.contentCardMarginFix}>
        {/* 비밀번호 설정 섹션 */}
        <form onSubmit={handleSubmit} name="passwordChangeForm" id="passwordChangeForm">
          {/* 접근성을 위한 숨겨진 사용자명 필드 */}
          <input
            type="text"
            name="username"
            autoComplete="username"
            className={styles.hiddenUsernameField}
            tabIndex={-1}
            aria-hidden="true"
          />
          <div className={styles.passwordSectionWrapper}>
            <table className={styles.passwordSection}>
              <tbody>
                <tr>
                  <td>기존 비밀번호</td>
                  <td>
                    <div className={styles.inputWrapper}>
                      <input
                        type={showPassword.current ? 'text' : 'password'}
                        name="currentPassword"
                        className={styles.input}
                        value={formData.currentPassword}
                        onChange={handleInputChange('currentPassword')}
                        placeholder="기존 비밀번호를 입력해주세요"
                        autoComplete="current-password"
                        form="passwordChangeForm"
                      />
                      <button
                        type="button"
                        className={styles.eyeButton}
                        onClick={toggleShowPassword('current')}
                        aria-label={showPassword.current ? '비밀번호 숨기기' : '비밀번호 보기'}
                      >
                        <img
                          src={showPassword.current ? eyeOpenIcon : eyeClosedIcon}
                          alt=""
                          className={styles.eyeIcon}
                        />
                      </button>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>새로운 비밀번호</td>
                  <td>
                    <div className={styles.inputWrapper}>
                      <input
                        type={showPassword.new ? 'text' : 'password'}
                        name="newPassword"
                        className={styles.input}
                        value={formData.newPassword}
                        onChange={handleInputChange('newPassword')}
                        placeholder="새로운 비밀번호를 입력해주세요"
                        autoComplete="new-password"
                        form="passwordChangeForm"
                      />
                      <button
                        type="button"
                        className={styles.eyeButton}
                        onClick={toggleShowPassword('new')}
                        aria-label={showPassword.new ? '비밀번호 숨기기' : '비밀번호 보기'}
                      >
                        <img
                          src={showPassword.new ? eyeOpenIcon : eyeClosedIcon}
                          alt=""
                          className={styles.eyeIcon}
                        />
                      </button>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>새로운 비밀번호 확인</td>
                  <td>
                    <div className={styles.inputWrapper}>
                      <input
                        type={showPassword.confirm ? 'text' : 'password'}
                        name="confirmPassword"
                        className={styles.input}
                        value={formData.confirmPassword}
                        onChange={handleInputChange('confirmPassword')}
                        placeholder="새로운 비밀번호를 다시 입력해주세요"
                        autoComplete="new-password"
                        form="passwordChangeForm"
                      />
                      <button
                        type="button"
                        className={styles.eyeButton}
                        onClick={toggleShowPassword('confirm')}
                        aria-label={showPassword.confirm ? '비밀번호 숨기기' : '비밀번호 보기'}
                      >
                        <img
                          src={showPassword.confirm ? eyeOpenIcon : eyeClosedIcon}
                          alt=""
                          className={styles.eyeIcon}
                        />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            {/* 비밀번호 생성 규칙 + 저장 버튼 */}
            <div className={styles.footerRow}>
              {/* 비밀번호 생성 규칙 안내 */}
              <div className={styles.passwordHint}>• 영문, 숫자, 특수문자 혼합 8자 이상</div>

              {/* 저장 버튼 */}
              <div className={styles.buttonGroup}>
                <Button
                  type="submit"
                  variant="default"
                  className={styles.saveButton}
                  disabled={isPending}
                >
                  {isPending ? '저장 중...' : '저장하기'}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </ContentCard>

      {/* Alert 컴포넌트 */}
      <Alert isOpen={alertState.isOpen} message={alertState.message} onClose={closeAlert} />
    </MainLayout>
  );
}

export default PasswordSettingPage;
